import React, { useEffect, useMemo, useState } from 'react';
import { objectFileName, uploadFile } from '../../../api/files';
import ActionButton from '../../../shared/components/action-button/ActionButton';
import ServiceCalendarColorField from '../../../shared/service-calendar-color/ServiceCalendarColorField';
import LocationAssignmentField from '../../../locations/components/LocationAssignmentField';
import { normalizeLocationIds } from '../../../locations/locationTypes';
import { normalizeCalendarColor } from '../../../shared/service-calendar-color/serviceCalendarColor';
import { normalizeEmployeeIds } from '../service-staff-assignment/serviceStaffAssignmentUtils';
import ServiceStaffGalleryPicker from '../service-staff-assignment/ServiceStaffGalleryPicker';
import '../service-staff-assignment/service-staff-assignment.css';

const normalize = (v) => String(v ?? '').trim();

const EMPTY_LOCATION_IDS = Object.freeze([]);

export default function ServiceBasicsEditorPage({
  initialService,
  companyId,
  busy = false,
  staffOptions = [],
  locationOptions = [],
  defaultLocationIds = EMPTY_LOCATION_IDS,
  contextLocationName = '',
  onCancel,
  onSave,
  onDelete,
}) {
  const isEdit = !!initialService?.id;
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    duration: 30,
    bufferTime: 0,
    price: 0,
    imageUrl: '',
    employeeIds: [],
    locationIds: [],
    calendarColor: null,
  });

  const locationOptionKey = useMemo(
    () => locationOptions.map((location) => location.id).sort().join('\u0001'),
    [locationOptions]
  );
  const defaultLocationKey = useMemo(
    () => normalizeLocationIds(defaultLocationIds).join('\u0001'),
    [defaultLocationIds]
  );
  const formSeedKey = isEdit
    ? `${initialService?.id || ''}:${initialService?.updatedAt || ''}`
    : `new:${locationOptionKey}:${defaultLocationKey}`;

  useEffect(() => {
    setUploading(false);
    const s = initialService || {};
    const normalizedLocationIds = normalizeLocationIds(s.locationIds);
    const fallbackLocationIds = normalizeLocationIds(defaultLocationIds).length > 0
      ? normalizeLocationIds(defaultLocationIds)
      : locationOptions.map((location) => location.id);
    setForm({
      name: s.name || '',
      description: s.description || '',
      duration: s.duration || 30,
      bufferTime: s.bufferTime ?? 0,
      price: s.price || 0,
      imageUrl: s.imageUrl || '',
      employeeIds: normalizeEmployeeIds(s.employeeIds),
      locationIds: normalizedLocationIds.length > 0
        ? normalizedLocationIds
        : fallbackLocationIds,
      calendarColor: normalizeCalendarColor(s.calendarColor),
    });
  }, [formSeedKey]);

  const isBusy = busy || uploading;
  const canSubmit = useMemo(() => normalize(form.name).length > 0 && !isBusy, [form.name, isBusy]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const basePath = companyId ? `companies/${companyId}/services` : 'services';
      const stored = await uploadFile(file, `${basePath}/${objectFileName(file)}`);
      const downloadUrl = stored.url;
      setForm((prev) => ({ ...prev, imageUrl: downloadUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    const name = normalize(form.name);
    if (!name) return;

    const duration = Math.max(1, parseInt(String(form.duration || 1), 10));
    const bufferTime = Math.max(0, parseInt(String(form.bufferTime || 0), 10));
    const price = Math.max(0, parseFloat(String(form.price || 0)));
    const employeeIds = normalizeEmployeeIds(form.employeeIds);

    if (staffOptions.length > 0 && employeeIds.length === 0) {
      const shouldContinue = window.confirm(
        'No employees are assigned to this service yet. It will not be bookable until you assign staff. Save anyway?'
      );
      if (!shouldContinue) return;
    }

    onSave?.({
      name,
      description: normalize(form.description),
      duration,
      bufferTime,
      price,
      imageUrl: form.imageUrl || '',
      employeeIds,
      locationIds: normalizeLocationIds(form.locationIds),
      calendarColor: normalizeCalendarColor(form.calendarColor),
    });
  };

  return (
    <div className="sd2-editorCard" role="region" aria-label={isEdit ? 'Edit service' : 'Service'}>
      <div className="sd2-editorCardHeader">
        <div style={{ minWidth: 0 }}>
          <div className="sd2-editorCardTitle">{isEdit ? 'Edit Service' : 'New Service'}</div>
          <div className="sd2-editorCardSub">
            {contextLocationName && !isEdit
              ? `Create a service offered at ${contextLocationName}.`
              : isEdit
                ? 'Update the name, description, duration, price, locations, image, and assigned staff.'
                : 'Set up a service clients can book online.'}
          </div>
        </div>
      </div>

      <div className="sd2-editorCardBody">
        <div className="sd2-formGrid">
          <section className="sd2-panel" aria-label="Image">
            <div className="sd2-field">
              <div className="sd2-label">Image</div>
              <label className="sd2-dropzone">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    handleImageUpload(file);
                    e.target.value = '';
                  }}
                  disabled={isBusy}
                />
                {form.imageUrl ? (
                  <img className="sd2-dropzoneImg" src={form.imageUrl} alt="" />
                ) : (
                  <div className="sd2-dropzoneEmpty">
                    <div className="sd2-dropzoneIcon">📷</div>
                    <div style={{ fontWeight: 800 }}>{uploading ? 'Uploading…' : 'Upload image'}</div>
                    <div className="sd2-hint">Optional</div>
                  </div>
                )}
              </label>
            </div>
          </section>

          <section className="sd2-panel" aria-label="Details">
            <div className="sd2-field">
              <label className="sd2-label" htmlFor="sd2-edit-name">
                Service name
              </label>
              <input
                id="sd2-edit-name"
                className="sd2-input"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Haircut"
                autoFocus
                disabled={isBusy}
              />
            </div>

            <div className="sd2-field">
              <label className="sd2-label" htmlFor="sd2-edit-description">
                Description
              </label>
              <textarea
                id="sd2-edit-description"
                className="sd2-textarea"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="What clients should know about this service…"
                rows={4}
                maxLength={1000}
                disabled={isBusy}
              />
              <div className="sd2-hint">Optional. Shown to clients when booking.</div>
            </div>

            <div className="sd2-field">
              <label className="sd2-label" htmlFor="sd2-edit-duration">
                Duration (minutes)
              </label>
              <input
                id="sd2-edit-duration"
                className="sd2-input"
                type="number"
                min={1}
                value={form.duration}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
                disabled={isBusy}
              />
              <div className="sd2-hint">Minimum 1 minute</div>
            </div>

            <div className="sd2-field">
              <label className="sd2-label" htmlFor="sd2-edit-price">
                Price
              </label>
              <input
                id="sd2-edit-price"
                className="sd2-input"
                type="number"
                min={0}
                step="0.01"
                value={form.price}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                disabled={isBusy}
              />
            </div>

            <div className="sd2-field">
              <label className="sd2-label" htmlFor="sd2-edit-buffer">
                Buffer time (minutes)
              </label>
              <input
                id="sd2-edit-buffer"
                className="sd2-input"
                type="number"
                min={0}
                step={1}
                value={form.bufferTime}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm((p) => ({ ...p, bufferTime: e.target.value }))}
                disabled={isBusy}
              />
              <div className="sd2-help">
                <strong>Buffer time</strong> adds extra time after the service for cleanup, setup, or breaks.
              </div>
            </div>

            <div className="sd2-field">
              <ServiceCalendarColorField
                value={form.calendarColor}
                onChange={(calendarColor) => setForm((p) => ({ ...p, calendarColor }))}
                disabled={isBusy}
              />
            </div>

            <div className="sd2-field">
              <LocationAssignmentField
                locations={locationOptions}
                value={form.locationIds}
                onChange={(locationIds) => setForm((prev) => ({ ...prev, locationIds }))}
                disabled={isBusy}
                label="Offered at locations"
                helperText="Where clients can book this service online."
              />
            </div>
          </section>
        </div>

        <section className="sd2-staffSection" aria-label="Assigned staff">
          <div className="sd2-label">Assigned staff</div>
          <ServiceStaffGalleryPicker
            employeeIds={form.employeeIds}
            onChange={(employeeIds) => setForm((prev) => ({ ...prev, employeeIds }))}
            staffOptions={staffOptions}
            locationOptions={locationOptions}
            locationIds={form.locationIds}
            disabled={isBusy}
          />
        </section>
      </div>

      <div className="sd2-editorCardFooter">
        {isEdit ? (
          <ActionButton
            type="button"
            onClick={onDelete}
            disabled={isBusy}
            style={{ marginRight: 'auto', borderColor: 'rgba(220,38,38,0.35)', color: '#b91c1c' }}
          >
            Delete service
          </ActionButton>
        ) : null}
        <ActionButton type="button" onClick={onCancel} disabled={isBusy}>
          Cancel
        </ActionButton>
        <ActionButton variant="primary" type="button" onClick={submit} disabled={!canSubmit || isBusy}>
          {isBusy ? 'Saving…' : isEdit ? 'Save changes' : 'Create service'}
        </ActionButton>
      </div>
    </div>
  );
}
